import React from 'react';
import { Badge } from '../../components/ui/badge';

interface LicenseUtilizationItem {
    id: string;
    system: string;
    totalLicenses: number;
    usedLicenses: number;
    utilization: number;
    monthlyCost: string;
    costPerLicense: string;
    potentialSavings: string;
}

interface LicenseUtilizationProps {
    systems: any[];
    loading: boolean;
}

const LicenseUtilization: React.FC<LicenseUtilizationProps> = ({ systems, loading }) => {
    const data = systems.map(system => {
        const total = parseInt(system.totalLicenses) || 0;
        const used = parseInt(system.usedLicenses) || 0;
        const monthly = parseFloat(system.monthlyCost) || 0;
        
        const utilization = total > 0 ? Math.round((used / total) * 100) : 0;
        const costPerLicense = total > 0 ? (monthly / total) : 0;
        const potentialSavings = (total - used) > 0 ? (total - used) * costPerLicense : 0;

        return {
            id: system.id,
            system: system.name,
            totalLicenses: total,
            usedLicenses: used,
            utilization,
            monthlyCost: `$${monthly.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            costPerLicense: `$${costPerLicense.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            potentialSavings: `$${potentialSavings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            savingsNumeric: potentialSavings
        };
    }).sort((a, b) => (b.savingsNumeric || 0) - (a.savingsNumeric || 0));

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h4 className="font-bold text-gray-800">License Utilization Overview</h4>
                    <p className="text-xs text-gray-500 mt-1">Monitor license usage to identify cost optimization opportunities</p>
                </div>

                {loading ? (
                    <div className="p-20 flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                    </div>
                ) : data.length === 0 ? (
                    <div className="p-20 text-center text-gray-500">
                        No system data available for analysis.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50/50 text-[11px] uppercase tracking-wider font-bold text-gray-400">
                                    <th className="px-6 py-4">System</th>
                                    <th className="px-6 py-4">Total Licenses</th>
                                    <th className="px-6 py-4">Used Licenses</th>
                                    <th className="px-6 py-4">Utilization</th>
                                    <th className="px-6 py-4">Monthly Cost</th>
                                    <th className="px-6 py-4">Cost per License</th>
                                    <th className="px-6 py-4">Potential Savings</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-bold text-gray-800">{item.system}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-gray-600">{item.totalLicenses}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-gray-600">{item.usedLicenses}</span>
                                        </td>
                                        <td className="px-6 py-4 min-w-[180px]">
                                            <div className="flex items-center gap-3">
                                                <div className="h-2 w-24 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-1000 ${
                                                            item.utilization < 30 ? "bg-red-500" : 
                                                            item.utilization < 70 ? "bg-amber-500" : "bg-green-500"
                                                        }`}
                                                        style={{ width: `${item.utilization}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-gray-600">{item.utilization}%</span>
                                                {item.utilization < 30 && (
                                                    <Badge className="bg-red-500 text-white border-0 text-[10px] h-5 font-bold uppercase tracking-tighter">
                                                        Low
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-bold text-gray-700">{item.monthlyCost}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-gray-600">{item.costPerLicense}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-sm font-bold ${item.savingsNumeric > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                                {item.potentialSavings}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LicenseUtilization;
